import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ChatAppSessionService } from './chat-app-session.service';

/** Endpoints whose refusal belongs to the page that called them, not to the session. */
const SESSION_ENTRY_PATHS = ['/auth/handoff/exchange', '/auth/refresher'];

const isSignInRequest = (url: string): boolean => /\/auth\/?$/.test(url.split('?')[0]);

/**
 * HTTP refusals that change where the user is — SPEC-2026-09-internal-chat-pwa (T-30).
 *
 * - `chat.errors.moduleDisabled` → the unavailable page, WITHOUT signing out: the module
 *   can come back, and signing out would be a lie about what happened (RF-03).
 * - any other 401 → the session ended (expired, revoked, deleted user): the shared exit
 *   runs, so the push registration is removed before the session is cleared (RF-29).
 *
 * Sign-in, the handoff exchange and the token renewal handle their own refusals.
 * Everything else is left to the screen that asked (they all have error states).
 */
@Injectable()
export class ChatAppHttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private readonly injector: Injector,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const ownRefusal = isSignInRequest(request.url) || SESSION_ENTRY_PATHS.some((path) => request.url.includes(path));
        if (!ownRefusal) {
          if (error?.error?.key === 'chat.errors.moduleDisabled') {
            this.zone.run(() => this.router.navigate(['/unavailable'], { queryParams: { reason: 'module' } }));
          } else if (error?.status === 401) {
            // Resolved lazily: the session service itself depends on HttpClient.
            this.injector.get(ChatAppSessionService).end('expired');
          }
        }
        return throwError(() => error);
      }),
    );
  }
}
